import { ICcypair } from '@alk/currency';
import { injectable } from 'inversify';
import { IPrice } from '@fnx/fx-pad';
import { ISessionDisposable, ISpotQuote, SpotPriceRequest, SpotPriceRequestWithClient } from '../../../interfaces';
import { ISpotStreamAdapterService } from './spot-stream-adapter.interface';
import { map } from 'rxjs/operators';
import { Observable, timer } from 'rxjs';

const INTERVAL_BETWEEN_QUOTE = 1000;
const MAX_RESPONSE_DELAY = 600;

@injectable()
export class SpotStreamAdapterMock implements ISpotStreamAdapterService, ISessionDisposable {
    private _subInitId = Math.floor(Math.random() * 100) * 1000;

    private readonly _feeds = new Map<number, SpotPriceRequestWithClient>();

    priceObservable(token: number): Observable<ISpotQuote> {
        return timer(MAX_RESPONSE_DELAY * Math.random(), INTERVAL_BETWEEN_QUOTE)
            .pipe(
                map(() => this.generatePrices(token)(this._feeds.get(token))),
            );
    }

    create = (request: SpotPriceRequestWithClient): number => {
        const token = this._subInitId++;
        this._feeds.set(token, request);
        return token;
    };

    delete = (token: number): void => {
        this._feeds.delete(token);
    };

    private readonly generateLegPrices = (id: number, ccyPair: ICcypair, quantity: number) : ISpotQuote => {
        const price = this.getRandomPrice(ccyPair, quantity || 1);
        return {
            requestId: id,
            askPrice: price.askPrice,
            bidPrice: price.bidPrice,
            spotSpread: {
                ExtraDigits: 2,
                Value: 4.33,
            },
        } as ISpotQuote;
    };

    private readonly generatePrices = (id: number) => (request: SpotPriceRequest): ISpotQuote => {
        const firstLegQuotes = this.generateLegPrices(id, request.ccyPair, request.quantity);

        if (request.spotType !== 'oco' && request.spotType !== 'ifDone') {
            return firstLegQuotes;
        } else if (request.quantity === request.quantitySecondLeg) {
            return [ firstLegQuotes, firstLegQuotes ] as ISpotQuote;
        }

        const secondLegQuotes = this.generateLegPrices(id, request.ccyPair, request.quantitySecondLeg);
        return [ firstLegQuotes, secondLegQuotes ] as ISpotQuote;
    };

    private getRandomPrice(instrument: string, quantity) {
        let rate = 0;
        let askRate = 0;
        let bidRate = 0;
        let priceSettings: IPrice = null;
        switch (instrument) {
            default:
            case 'EURUSD':
                rate = (1.08385 + Math.random() / 10000 + Math.log(quantity) / 10000);
                askRate = rate + (Math.log(quantity) / 10000) + (Math.random() / 100000);
                bidRate = rate - (Math.log(quantity) / 10000) + (Math.random() / 100000);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 2,
                    FractionSize: 4,
                };
                break;
            case 'EURGBP':
                rate = (0.87064 + Math.random() / 10000 + Math.log(quantity) / 10000);
                askRate = rate + (Math.log(quantity) / 100000) + (Math.random() / 100000);
                bidRate = rate - (Math.log(quantity) / 100000) + (Math.random() / 100000);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 2,
                    FractionSize: 3,
                };

                break;
            case 'USDJPY':
                rate = 106.56 + Math.random() / 10 + Math.log(quantity) / 10000;
                askRate = rate + (Math.log(quantity) / 1000) + (Math.random() / 100);
                bidRate = rate - (Math.log(quantity) / 1000) + (Math.random() / 100);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 0,
                    FractionSize: 2,
                };

                break;
            case 'USDRUB':
                rate = 73.7768 + Math.random() / 100 + Math.log(quantity) / 10000;
                askRate = rate + (Math.log(quantity) / 1000) + (Math.random() / 1000);
                bidRate = rate - (Math.log(quantity) / 1000) + (Math.random() / 1000);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 0,
                    FractionSize: 4,
                };
                break;
            case '100GBP':
                rate = 117.57 + Math.random() + Math.log(quantity) / 10000;
                askRate = rate + (Math.log(quantity) / 1000) + (Math.random());
                bidRate = rate - (Math.log(quantity) / 1000) + (Math.random());

                priceSettings = {
                    Value: null,
                    ExtraDigits: 1,
                    FractionSize: 1,
                };
                break;
            case 'USDVND':
                rate = 23084.18 + Math.random() * 10 + Math.log(quantity) / 10000;
                askRate = rate + (Math.log(quantity) / 1000) + (Math.random() * 10);
                bidRate = rate - (Math.log(quantity) / 1000) + (Math.random() * 10);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 1,
                    FractionSize: 0,
                };
                break;
        }

        const askPrice = {
            ...priceSettings,
            Value: askRate,
        };
        const bidPrice = {
            ...priceSettings,
            Value: bidRate,
        };

        return {
            askPrice: askPrice,
            bidPrice: bidPrice,

        };
    }

    stop: () => void;
}
